import { useEffect, useState } from "react";
import { AlertOctagon, ArrowLeft, Camera, Check, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLocalization } from "@/context/localization-context";
import type { TranslationKey } from "@/i18n/messages";
import { cn } from "@/lib/utils";
import { capture, errorMessage, httpStatus, PHOTO_SCALE, type StationProduct } from "./api";
import { MiniBadge } from "./mini-badge";
import { PhotoView, PhotoViewer } from "./photos";
import { pizzaLabel } from "./products";
import { usePhoto } from "./use-photo";

/** once the photo is shown (or did not come), the picker comes back by itself after this many seconds */
const NEXT_AFTER_S = 10;

type State = { kind: "ready" } | { kind: "capturing" } | { kind: "done"; pizzaId: string } | { kind: "failed"; message: string };

/** a text the staff understands for the backend's refusal of a capture */
function failure(error: unknown): { key: TranslationKey; detail: string } {
  const status = httpStatus(error);
  const detail = errorMessage(error);
  if (status === undefined) return { key: "station.capture.offline", detail };
  if (status === 404 && detail.startsWith("No camera")) return { key: "station.capture.noCamera", detail };
  if (status === 502) return { key: "station.capture.cameraFailed", detail };
  if (status === 409) return { key: "station.capture.disabled", detail };
  if (status === 400 && detail === "branch_id is required") return { key: "station.capture.noBranch", detail };
  return { key: "station.capture.failed", detail };
}

/** The picked pizza and the big "Photograph" button; then the photo the camera took, or why not. */
export function CaptureScreen({ product, onDone }: { product: StationProduct; onDone: () => void }) {
  const { t } = useLocalization();
  const [state, setState] = useState<State>({ kind: "ready" });
  const label = pizzaLabel(product.name);

  const photograph = async () => {
    setState({ kind: "capturing" });
    try {
      const { pizza_id } = await capture(product.id);
      setState({ kind: "done", pizzaId: pizza_id });
    } catch (e) {
      const { key, detail } = failure(e);
      setState({ kind: "failed", message: `${t(key)} (${detail})` });
    }
  };

  return (
    <div className="flex h-full flex-col items-center justify-center gap-6 overflow-y-auto p-6 text-center">
      <div className="flex flex-col items-center gap-2">
        <span className="text-sm font-medium uppercase tracking-wide text-muted-foreground">{t("station.capture.picked")}</span>
        <h1 className="flex max-w-3xl flex-wrap items-center justify-center gap-3 break-words text-3xl font-semibold leading-tight sm:text-4xl">
          {label.mini && <MiniBadge />}
          {label.title}
        </h1>
        {product.ingredients.length > 0 && <p className="max-w-2xl text-base text-muted-foreground">{product.ingredients.join(", ")}</p>}
      </div>

      {state.kind === "done" ? (
        <Photographed pizzaId={state.pizzaId} title={product.name} onNext={onDone} />
      ) : (
        <>
          <button
            type="button"
            onClick={() => void photograph()}
            disabled={state.kind === "capturing"}
            className={cn(
              "flex h-48 w-48 flex-col items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground shadow-[0_8px_24px_rgba(0,0,0,0.5)]",
              "transition-transform active:scale-95 disabled:opacity-80 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-ring",
            )}
          >
            {state.kind === "capturing" ? (
              <Loader2 className="h-16 w-16 animate-spin" aria-hidden />
            ) : (
              <Camera className="h-16 w-16" aria-hidden />
            )}
            <span className="text-xl font-semibold">
              {state.kind === "capturing" ? t("station.capture.capturing") : t("station.capture.photograph")}
            </span>
          </button>

          {state.kind === "failed" && (
            <p role="alert" className="flex max-w-xl items-start gap-2 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-left text-base text-red-300">
              <AlertOctagon className="mt-0.5 h-5 w-5 shrink-0" aria-hidden />
              <span className="min-w-0 break-words">{state.message}</span>
            </p>
          )}

          <Button variant="ghost" className="h-12 gap-2 rounded-full px-6 text-base" onClick={onDone} disabled={state.kind === "capturing"}>
            <ArrowLeft className="h-5 w-5" />
            {t("station.capture.change")}
          </Button>
        </>
      )}
    </div>
  );
}

/**
 * "Photographed" and the photo as soon as the camera's upload arrives; then the picker comes back
 * by itself, unless the staff opened the photo over the whole screen to look at it.
 */
function Photographed({ pizzaId, title, onNext }: { pizzaId: string; title: string; onNext: () => void }) {
  const { t } = useLocalization();
  const photo = usePhoto(pizzaId, PHOTO_SCALE.screen, true);
  const [viewing, setViewing] = useState(false);
  const [held, setHeld] = useState(false);
  const [left, setLeft] = useState(NEXT_AFTER_S);
  const counting = photo.kind !== "loading" && !held;

  useEffect(() => {
    if (!counting) return;
    const timer = window.setInterval(() => setLeft((seconds) => seconds - 1), 1000);
    return () => window.clearInterval(timer);
  }, [counting]);

  useEffect(() => {
    if (left <= 0) onNext();
  }, [left, onNext]);

  const open = () => {
    if (photo.kind !== "ready") return;
    setHeld(true);
    setViewing(true);
  };

  return (
    <div className="flex w-full max-w-3xl flex-col items-center gap-4" role="status">
      <div className="flex items-center gap-2 text-2xl font-semibold text-emerald-300">
        <Check className="h-8 w-8" strokeWidth={2.5} aria-hidden />
        {t("station.capture.done")}
      </div>
      <button
        type="button"
        onClick={open}
        className="w-full overflow-hidden rounded-2xl border bg-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        aria-label={t("station.photo.open")}
      >
        <PhotoView photo={photo} alt={title} className="mx-auto max-h-[50dvh] min-h-[12rem] w-full object-contain" />
      </button>
      <Button className="h-14 min-w-[14rem] rounded-full px-8 text-lg" onClick={onNext}>
        {counting ? t("station.capture.nextIn", { seconds: Math.max(left, 0) }) : t("station.capture.next")}
      </Button>
      {viewing && photo.kind === "ready" && (
        <PhotoViewer title={title} onClose={() => setViewing(false)}>
          <img src={photo.url} alt={title} className="max-h-full max-w-full rounded-lg object-contain" draggable={false} />
        </PhotoViewer>
      )}
    </div>
  );
}
